import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub } from '@/lib/hub-client';
import { PasswordInput } from '@/pages/auth/SetPasswordForm';
import { SecurityCard } from './Security';

export const PASSWORD_CHANGED_MESSAGE = 'Password changed. Other devices were signed out.';
export const SAME_PASSWORD_MESSAGE = 'The new password must differ from the current one.';
const WRONG_CURRENT_MESSAGE = 'Current password is incorrect.';

/**
 * /me/account — the signed-in user's own account page. Today it holds one
 * thing: change password (current + new). The hub verifies the current
 * password, rotates the hash and revokes every refresh token
 * (POST /auth/password/change); this device keeps its access cookie until
 * it expires.
 */
export function Account() {
  return (
    <div className="w-full space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Account</h1>
        <p className="text-sm text-muted-foreground">Your sign-in settings.</p>
      </div>
      <ChangePasswordCard />
      <SecurityCard />
    </div>
  );
}

export function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (newPassword === currentPassword) {
      setError(SAME_PASSWORD_MESSAGE);
      return;
    }
    setPending(true);
    try {
      await hub.authChangePassword({ body: { currentPassword, newPassword } });
      setCurrentPassword('');
      setNewPassword('');
      setDone(true);
    } catch (err) {
      const wrong = err instanceof HubClientError && err.code === 'invalid_credentials';
      setError(wrong ? WRONG_CURRENT_MESSAGE : ((err as Error).message ?? 'Could not change the password'));
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="max-w-md rounded-lg border bg-card p-5">
      <div className="space-y-1.5">
        <h2 className="text-base font-medium">Change password</h2>
        <p className="text-sm text-muted-foreground">
          Type your current password, then choose a new one of at least 8 characters.
        </p>
      </div>
      <form className="mt-4 space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="current-password">Current password</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <PasswordInput
            id="new-password"
            value={newPassword}
            onChange={setNewPassword}
            revealed={revealed}
            onToggle={() => setRevealed((r) => !r)}
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {done && <p className="text-sm text-emerald-700 dark:text-emerald-400">{PASSWORD_CHANGED_MESSAGE}</p>}
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </section>
  );
}
